/** The world-folder-backed content endpoints: GM `.lcp` packs, engine sidecars, terrain presets. */

/**
 * The **world content-pack** REST endpoint — delivery is a server-seeded world pack.
 * A client GETs this once before opening its rooms (it
 * composes with the same pre-connect step as {@link GAME_SLOTS_PATH}) and receives
 * the GM-supplied `.lcp` packs the server read from `<world>/packs/`, each as
 * base64-encoded bytes. The client decodes + merges them into its **live in-memory**
 * Compendium only (never IndexedDB). An empty list means the world has
 * no GM packs (the absence-UX path: the encounter-builder disables NPC spawning).
 *
 * Why base64 over JSON rather than a binary stream: the bytes are already validated
 * server-side and modest in size (the official NPC `.lcp` is ~90 KB), the client's
 * existing fetch/JSON plane (`lib/api.ts`) carries it with no new transport, and the
 * payload mirrors the existing serializable `PackLoadCommand` shape.
 */
export const WORLD_PACKS_PATH = "/api/world/packs" as const;
/**
 * The **world sidecar-pack** REST endpoint — the client-side sidecar-overlay load.
 * Mirrors {@link WORLD_PACKS_PATH} exactly, but for the *engine-logic* sidecar packs the
 * server reads from `<world>/sidecars/*` rather than the GM's `.lcp` content packs. A
 * client GETs this once (composing with the same
 * pre-connect step as the other world-* endpoints) and gets back every sidecar the server
 * validated at boot (a broken sidecar is logged-and-skipped server-side, never served — see
 * `backend/src/world/rooms.ts`'s `WorldRooms.boot`). The client runs the SAME
 * `parseSidecar`/`loadSidecarChain`/`mergeOverlays(SEED_OVERLAY, ...)` pipeline the server ran to
 * build its own overlay, so `enumerateActions`'s `resolvable` classification matches what the
 * server will actually accept on `request_activate` — closing the gap where the client used to
 * fall back to the bare intrinsic `SEED_OVERLAY` and silently mis-render every authored deployable
 * as advisory.
 *
 * Unlike {@link WORLD_PACKS_PATH}'s `.lcp` bytes (opaque binary, base64-hopped for JSON transport),
 * a sidecar's files ({@link WorldSidecarPayload.files} — `manifest.json` + `overlay/<slug>.json`)
 * are already plain JSON *text*. There's no binary payload to encode, so this endpoint carries the
 * file map as plain strings — a base64 hop would only add decode work for zero transport benefit.
 */
export const WORLD_SIDECARS_PATH = "/api/world/sidecars" as const;
/**
 * The **terrain-preset (scene) library** REST endpoint — a per-encounter terrain preset
 * library. Server-side, world-folder-backed (`<world>/scenes/<name>.json`), and
 * **GM-only** for the mutating verbs (save/delete pass the host key in the
 * `x-lvtt-host-key` header — the same operator credential the Game-room `game_hello`
 * checks). A preset is just the terrain layer (cell terrain + edge walls + map
 * radius), reusable across encounters because it carries no combatants. The actual
 * *load into an encounter* is not this endpoint — it's the one undoable
 * `apply_terrain_map` encounter command the client builds from the fetched preset.
 *
 *   GET    /api/world/scenes            → {@link SceneListResponse} (names; public)
 *   GET    /api/world/scenes/<name>     → {@link ScenePresetPayload} (one preset; public read)
 *   PUT    /api/world/scenes/<name>     → save (GM host key); body is a {@link ScenePresetPayload}
 *   DELETE /api/world/scenes/<name>     → delete (GM host key)
 */
export const WORLD_SCENES_PATH = "/api/world/scenes" as const;

/**
 * The **campaign codex content** endpoint — the authored entries (people, places,
 * factions, ships) and their facts the server read from `<world>/codex.json` and
 * validated at boot. Public read, no write verb: the codex is authored content the
 * server never mutates (mirrors {@link WORLD_PACKS_PATH}'s read-only channel). The
 * response body is a `CodexContent` from `@lancer-vtt/domain`; a world with no codex
 * answers an empty entry list.
 *
 * **One payload, every recipient.** There is no reveal state, no host-key branch and no
 * per-viewer projection — the file is served whole, so a client fetches this once and
 * renders it. Issuing a new record to the table is an edit to `<world>/codex.json`
 * (`codex/README.md`'s two-file convention), not a runtime command.
 */
export const WORLD_CODEX_PATH = "/api/world/codex" as const;

/** One saved terrain preset as carried over {@link WORLD_SCENES_PATH} (JSON; the world-folder shape). */
export type ScenePresetPayload = {
  name: string;
  /** The whole-cell terrain layer, keyed by `"q,r"` cell key. */
  terrain: Record<string, unknown>;
  /** The per-edge wall layer, keyed by the canonical edge key. */
  edges: Record<string, unknown>;
  /** The map's bound radius (hex-disk around the origin) — the bound the *hex* grid modes use. */
  mapRadius: number;
  /**
   * The **square grid's extent** the preset was authored at, when it was authored on
   * a `2d-square` board. Carried alongside `mapRadius` for the same reason the
   * encounter stores both: they are two independent bounds, and `mapRadius` says
   * nothing about a rectangle. Without these a 40×20 preset would load its terrain
   * onto whatever extent the target encounter happened to have — cells silently
   * stranded off-board, or a board far larger than the map that fills it.
   *
   * **Additive/optional** — a preset saved before this field (or one authored on a hex
   * board) simply omits it, and loads exactly as it did before: extent untouched.
   */
  gridWidth?: number;
  gridHeight?: number;
}

/** The {@link WORLD_SCENES_PATH} list response: every saved preset's name (possibly none). */
export type SceneListResponse = {
  scenes: string[];
}

/** One GM-supplied content pack as served over {@link WORLD_PACKS_PATH}: filename + base64 `.lcp` bytes. */
export type WorldPackPayload = {
  name: string;
  /** The raw `.lcp` ZIP bytes, base64-encoded for JSON transport. */
  bytesBase64: string;
}

/** The {@link WORLD_PACKS_PATH} response: every GM pack the world server holds (possibly none). */
export type WorldPacksResponse = {
  packs: WorldPackPayload[];
}

/**
 * One world sidecar pack as served over {@link WORLD_SIDECARS_PATH}: its name (for diagnostics)
 * plus its raw manifest/overlay files — plain JSON text, keyed by relative path (`"manifest.json"`,
 * `"overlay/<slug>.json"`), the exact shape `@lancer-vtt/rules`' `parseSidecar` consumes.
 */
export type WorldSidecarPayload = {
  name: string;
  files: Record<string, string>;
}

/**
 * One overlay entry a world sidecar's boot-time load rejected or skipped, by id + a
 * human-facing reason. Shared shape for both {@link WorldSidecarLoadEntry.rejected} (schema
 * gate) and {@link WorldSidecarLoadEntry.unresolvable} (content gate) — the two per-entry
 * buckets `decisions/0135` and its 2026-09-03 amendment define.
 */
export type WorldSidecarEntryIssue = {
  id: string;
  reason: string;
}

/**
 * One world sidecar's load outcome: how many of its entries loaded, and the two per-entry
 * reject buckets. `accepted` is the entry count that made it into the merged overlay — the
 * same number {@link WorldSidecarLoadReport.acceptedEntryCount} sums across every sidecar.
 * `rejected` is a schema-gate miss (`parseSidecar`'s per-entry `OverlayEntrySchema` failure);
 * `unresolvable` is a content-gate miss (`partitionSidecarContent` in `@lancer-vtt/rules` — an
 * id no loaded pack provides). Both are per-entity facts, and neither drops the sidecar itself.
 */
export type WorldSidecarLoadEntry = {
  name: string;
  accepted: number;
  rejected: WorldSidecarEntryIssue[];
  unresolvable: WorldSidecarEntryIssue[];
}

/**
 * The world's whole sidecar-load outcome, computed once at boot (`WorldRooms.boot`) and held
 * as `WorldRooms.sidecarLoadReport` — the machine-readable status surface `lvtt world sidecars`
 * and the proofs read (`decisions/0135`'s 2026-09-03 amendment). `acceptedEntryCount` is the
 * SAME number `sidecarAcceptedEntryCount` already is (the sidecarless-boot backstop's input) —
 * this report doesn't replace that field, it explains it. `dropped` is the pack-level
 * structural-failure bucket (a bad manifest, a stray file, a missing-content THROW) — sidecars
 * that never got recorded anywhere before this report existed.
 */
export type WorldSidecarLoadReport = {
  acceptedEntryCount: number;
  sidecars: WorldSidecarLoadEntry[];
  dropped: { name: string; reason: string }[];
  /**
   * World sidecar copies that differ from the repo's authored pack (`backend/src/world/sidecarStaleness.ts`),
   * each with the command that restages it. Absent from a server that predates the check, and empty
   * on a deployed box, which ships no `packs/` to compare against.
   */
  stale?: StaleWorldSidecar[];
}

/** One world sidecar copy that no longer matches its authored pack, and how to restage it. */
export type StaleWorldSidecar = {
  name: string;
  /** The shell command, run from the code checkout, that restages the pack into this world. */
  restage: string;
}

/**
 * The {@link WORLD_SIDECARS_PATH} response: every validated world sidecar pack the server
 * holds (possibly none), plus the additive {@link load} report (`decisions/0135`'s 2026-09-03
 * amendment) — a client that only wants the bytes (the CLI's existing sidecar-merge path,
 * `frontend/src/lib/compendium.ts`) ignores it; `lvtt world sidecars` reads it directly.
 */
export type WorldSidecarsResponse = {
  sidecars: WorldSidecarPayload[];
  load: WorldSidecarLoadReport;
}